import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

import { Button } from "./button";
import { GroundingFile } from "@/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function fixImageUrls(markdown: string) {
    return markdown.replace(/!\[([^\]]*)\]\((\/hc\/article_attachments\/[^)]+)\)/g, "![$1](https://support.planetdds.com$2)");
}

type Properties = {
    groundingFile: GroundingFile | null;
    onClosed: () => void;
};

export default function GroundingFileView({ groundingFile, onClosed }: Properties) {
    return (
        <AnimatePresence>
            {groundingFile && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
                    onClick={() => onClosed()}
                >
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        className="flex max-h-[95vh] w-full max-w-4xl flex-col rounded-2xl bg-white p-6 text-gray-900"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-center justify-end">
                            <Button
                                aria-label="Close grounding file view"
                                variant="ghost"
                                size="sm"
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => onClosed()}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="flex-grow overflow-hidden">
                            <div className="h-[80vh] overflow-auto text-wrap rounded-lg bg-gray-100 p-6 text-lg text-gray-900">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        img: ({ node, ...props }) => (
                                            <img
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "40vh",
                                                    display: "block",
                                                    margin: "2rem auto",
                                                    borderRadius: "1rem",
                                                    boxShadow: "0 4px 24px rgba(0,0,0,0.10)"
                                                }}
                                                {...props}
                                            />
                                        ),
                                        h1: ({ node, ...props }) => (
                                            <h1 style={{ fontSize: "2em", fontWeight: 700, margin: "1em 0", lineHeight: 1.2 }} {...props} />
                                        ),
                                        h2: ({ node, ...props }) => (
                                            <h2 style={{ fontSize: "1.5em", fontWeight: 700, margin: "1em 0", lineHeight: 1.2 }} {...props} />
                                        ),
                                        h3: ({ node, ...props }) => (
                                            <h3 style={{ fontSize: "1.2em", fontWeight: 700, margin: "1em 0", lineHeight: 1.2 }} {...props} />
                                        ),
                                        p: ({ node, ...props }) => <p style={{ fontSize: "1.15em", marginBottom: "1em", lineHeight: 1.3 }} {...props} />,
                                        li: ({ node, ...props }) => <li style={{ fontSize: "1.1em", marginBottom: "0.5em" }} {...props} />
                                    }}
                                >
                                    {fixImageUrls(groundingFile.content)}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
